// src/composables/usePersistentStorage.ts
import { onMounted, ref } from 'vue'

export function usePersistentStorage() {
  // A reactive flag that indicates whether persistent storage is granted.
  const isPersistent = ref<boolean>(false)
  // A reactive variable to hold an error message (if any).
  const error = ref<string | null>(null)

  async function checkPersistentStorage(): Promise<void> {
    try {
      // @ts-expect-error the error is saying that persist is always available, which is not true
      if (navigator.storage && navigator.storage.persist) {
        const persisted = await navigator.storage.persisted()
        isPersistent.value = persisted
      } else {
        isPersistent.value = false
      }
    } catch (err: unknown) {
      error.value = (err as Error).message
      isPersistent.value = false
    }
  }

  /**
   * Checks persistent storage permission.
   * - If persistent storage is already enabled, sets isPersistent to true.
   * - Otherwise, it calls navigator.storage.persist() to request persistent storage.
   *
   * @returns A Promise that resolves to a boolean indicating whether persistent storage is granted.
   */
  async function tryPersistingStorage(): Promise<boolean> {
    if (isPersistent.value) {
      return true
    }
    // Check if the StorageManager API is supported.
    if (
      'storage' in navigator &&
      'persisted' in navigator.storage &&
      'persist' in navigator.storage
    ) {
      const granted = await navigator.storage.persist()
      isPersistent.value = granted
      return granted
    } else {
      // Fallback for browsers that do not support the StorageManager API.
      error.value = 'Persistent storage is not supported in this browser.'
      isPersistent.value = false
      return false
    }
  }

  onMounted(() => checkPersistentStorage())

  return { isPersistent, error, checkPersistentStorage, tryPersistingStorage }
}
